import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { TrainingAssignment, SellerWithProfile } from '../types';

export const useTrainingAssignments = (sellerId?: string) => {
  const queryClient = useQueryClient();

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['training-assignments', sellerId],
    queryFn: async () => {
      let query = supabase
        .from('training_assignments')
        .select('*')
        .order('assigned_at', { ascending: true });

      if (sellerId) {
        query = query.eq('seller_id', sellerId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as TrainingAssignment[];
    },
  });

  const { data: sellers = [] } = useQuery({
    queryKey: ['sellers-for-training'],
    queryFn: async () => {
      // Get all users with vendedor role
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'vendedor');

      if (rolesError) throw rolesError;

      const sellerIds = roles.map(r => r.user_id);
      if (sellerIds.length === 0) return [];

      // Get profiles for those sellers
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, nombre, ubicacion_tienda')
        .in('user_id', sellerIds);

      if (profilesError) throw profilesError;
      return profiles as SellerWithProfile[];
    },
  });

  const assignModules = useMutation({
    mutationFn: async ({ 
      sellerId, 
      moduleIds, 
      expectedCompletion 
    }: { 
      sellerId: string; 
      moduleIds: string[]; 
      expectedCompletion: string | null;
    }) => {
      const { data: user } = await supabase.auth.getUser();
      
      const assignments = moduleIds.map((moduleId, index) => ({
        seller_id: sellerId,
        module_id: moduleId,
        assigned_by: user.user!.id,
        expected_completion: expectedCompletion,
        status: index === 0 ? 'pending' : 'locked', // First module is unlocked
      }));

      const { data, error } = await supabase
        .from('training_assignments')
        .insert(assignments)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-assignments'] });
      toast.success('Módulos asignados exitosamente');
    },
    onError: (error) => {
      toast.error('Error al asignar: ' + error.message);
    },
  });

  const updateAssignment = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<TrainingAssignment> & { id: string }) => {
      const { data, error } = await supabase
        .from('training_assignments')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-assignments'] });
    },
  });

  const unlockNextModule = useMutation({
    mutationFn: async ({ sellerId, currentModuleOrder }: { sellerId: string; currentModuleOrder: number }) => {
      // Find the next locked module for this seller
      const { data: nextAssignment, error: findError } = await supabase
        .from('training_assignments')
        .select('*, training_modules!inner(order_position)')
        .eq('seller_id', sellerId)
        .eq('status', 'locked')
        .order('training_modules(order_position)', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (findError) throw findError;
      if (!nextAssignment) return null;

      const { data, error } = await supabase
        .from('training_assignments')
        .update({ status: 'pending' })
        .eq('id', nextAssignment.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-assignments'] });
    },
  });

  return {
    assignments,
    sellers,
    isLoading,
    assignModules,
    updateAssignment,
    unlockNextModule,
  };
};
